import { CSSProperties, useState } from 'react';
import useWidthBreakpoint from '../../../../../hooks/useWidthBreakpoint';
import { useResultData } from '../context';
import { tokens } from '../tokens';
import { pick } from '../responsive';
import {
  buildLiveStateFromCurrent,
  episodeToPhases,
} from '../derived/mrcEpisodes';
import { isStopLoss, isTakeProfit } from '../derived/format';
import EditorialHeader from './shared/EditorialHeader';
import DeepRow from './shared/DeepRow';
import BalancePill from './shared/BalancePill';
import MrcPhaseFlow from './shared/MrcPhaseFlow';
import ChartStack, { RsiDescriptor } from './charts/ChartStack';
import FilterRow, { TxFilters } from './TransactionList/FilterRow';

interface Props {
  filters: TxFilters;
  onFiltersChange: (next: TxFilters) => void;
}

const LiveState = ({ filters, onFiltersChange }: Props) => {
  const displayData = useResultData();
  const [mrcMode, setMrcMode] = useState(false);
  // The MRC episode of the current (latest core) position drives the deep
  // dive — exactly like an expanded transaction's deep dive.
  const liveCore = buildLiveStateFromCurrent(displayData.raw);
  const lastCoreTimestamp = liveCore
    ? liveCore.transactionTimestamp ?? liveCore.timestamp
    : 0;
  // The live MRC deep dive shows the episode currently in progress — the one
  // that initiated AFTER the latest core transaction, never the historical
  // episode that resolved into that core.
  const mrcEpisode = liveCore
    ? [...(displayData.raw.mrcStates ?? [])]
        .filter((episode) => episode.startTimestamp > lastCoreTimestamp)
        .sort((a, b) => a.startTimestamp - b.startTimestamp)
        .pop()
    : undefined;
  const mrcPhases = mrcEpisode ? episodeToPhases(mrcEpisode) : [];
  // Only offer the MRC deep dive when there is a current MRC episode.
  const hasMrc = !!mrcEpisode;
  const { breakpoint } = useWidthBreakpoint();
  // Live accent follows the position direction: SELL = "in stable, waiting"
  // (green / no exposure); BUY = "in position, exposed" (red).
  const liveAccent = liveCore?.orderType === 'BUY' ? tokens.red : tokens.green;
  // Current profit color tracks its sign rather than the position direction.
  const currentProfitNum = displayData.raw.currentProfit ?? 0;
  const currentProfitColor = currentProfitNum >= 0 ? tokens.green : tokens.red;

  // Hide TP/SL filter chips when the transaction history doesn't include any —
  // avoids dangling filters that can't possibly match anything.
  const allTransactions = displayData.raw.transactions ?? [];
  const hasTakeProfit = allTransactions.some((tx) =>
    isTakeProfit(tx.transactionType),
  );
  const hasStopLoss = allTransactions.some((tx) =>
    isStopLoss(tx.transactionType),
  );

  const padding = pick(breakpoint, {
    sm: '4px 0 0',
    md: '4px 0 0',
    lg: '4px 0 0',
  });

  // Layout: md+lg = horizontal split (rail 360 + chart); sm = vertical stack
  const containerLayout: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      display: 'flex',
      flexDirection: 'column',
      borderTop: '1px solid rgba(255,255,255,0.22)',
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    md: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: '1px solid rgba(255,255,255,0.22)',
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
    lg: {
      display: 'flex',
      alignItems: 'flex-start',
      borderTop: '1px solid rgba(255,255,255,0.22)',
      borderBottom: '1px solid rgba(255,255,255,0.22)',
    },
  });

  const accentBar: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      height: 3,
      background: liveAccent,
      flexShrink: 0,
      position: 'relative',
    },
    md: {
      width: 3,
      background: liveAccent,
      flexShrink: 0,
      position: 'relative',
      alignSelf: 'stretch',
    },
    lg: {
      width: 3,
      background: liveAccent,
      flexShrink: 0,
      position: 'relative',
      alignSelf: 'stretch',
    },
  });

  const accentDotPosition: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { left: -3, top: -3 },
    md: { left: -3, top: -3 },
    lg: { left: -3, top: -3 },
  });

  const railStyle: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: {
      width: '100%',
      background: '#000',
      padding: '20px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    md: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '24px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
    },
    lg: {
      width: 360,
      flexShrink: 0,
      background: '#000',
      padding: '24px 28px 24px 26px',
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      borderRight: '1px solid rgba(255,255,255,0.08)',
    },
  });

  const rightSide: CSSProperties = pick<CSSProperties>(breakpoint, {
    sm: { display: 'flex', flexDirection: 'column' },
    md: { flex: 1, display: 'flex', flexDirection: 'column' },
    lg: { flex: 1, display: 'flex', flexDirection: 'column' },
  });

  const chartHeight = pick(breakpoint, { sm: 240, md: 360, lg: 420 });

  // Only mount RSI panels that actually have data — never an empty chart.
  const chartRsis: RsiDescriptor[] = mrcMode
    ? ([
        displayData.raw.mrcRsiHigherTValues?.length && {
          key: 'mrcHigher',
          side: 'mrcHigher',
          label: `HIGHER T MRC RSI · ${displayData.timeframes.higherT}`,
        },
        displayData.raw.mrcRsiShorterTValues?.length && {
          key: 'mrcShorter',
          side: 'mrcShorter',
          label: `SHORTER T MRC RSI · ${displayData.timeframes.shorterT}`,
        },
        displayData.raw.mrcRsiMultiTValues?.length && {
          key: 'mrcMulti',
          side: 'mrcMulti',
          label: `MULTI T MRC RSI · ${displayData.timeframes.multiT}`,
        },
      ].filter(Boolean) as RsiDescriptor[])
    : displayData.raw.indicatorConfig
    ? [
        {
          key: 'opposite',
          side: 'opposite',
          label: `OPPOSITE ORDER RSI · p:${displayData.indicatorConfig.opposite.parentPeriod} · threshold ${displayData.indicatorConfig.opposite.parentThreshold}`,
        },
        {
          key: 'same',
          side: 'same',
          label: `SAME ORDER RSI · p:${displayData.indicatorConfig.same.parentPeriod} · threshold ${displayData.indicatorConfig.same.parentThreshold}`,
        },
      ]
    : [];

  return (
    <div
      style={{
        // Transparent so the subscription page's profit wash shows behind the
        // top (on the dev Result page the page bg is already #000).
        background: 'transparent',
        padding,
        color: tokens.textPrimary,
        fontFamily: tokens.fontSans,
        maxWidth: 1400,
        margin: '0 auto',
      }}
    >
      <EditorialHeader number="05" label="Current State · Transaction List" />

      <FilterRow
        filters={filters}
        onChange={onFiltersChange}
        hasTakeProfit={hasTakeProfit}
        hasStopLoss={hasStopLoss}
      />

      <div style={containerLayout}>
        {/* Live accent bar with glowing dot */}
        <div style={accentBar}>
          <div
            style={{
              ...accentDotPosition,
              position: 'absolute',
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: liveAccent,
              boxShadow: `0 0 12px ${liveAccent}`,
            }}
          />
        </div>

        <div style={railStyle}>
          {/* LIVE badge + meta */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span
                className="nc-live-glow"
                style={{
                  fontSize: 22,
                  fontFamily: tokens.fontMono,
                  color: liveAccent,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: liveAccent,
                    boxShadow: `0 0 8px ${liveAccent}`,
                  }}
                />
                LIVE
              </span>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    color: tokens.textPrimary,
                    fontWeight: 600,
                  }}
                >
                  Current state
                </span>
                <span
                  style={{
                    fontSize: 10,
                    color: tokens.textMuted,
                    fontFamily: tokens.fontMono,
                    letterSpacing: '0.1em',
                    marginTop: 2,
                  }}
                >
                  OPEN POSITION
                </span>
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: tokens.textTertiary,
                fontFamily: tokens.fontMono,
                marginTop: 4,
              }}
            >
              {displayData.liveState.openedAt} ·{' '}
              {displayData.liveState.openDurationDays} days
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }} />

          {/* Hero values */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: tokens.textSecondary,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  fontWeight: 600,
                }}
              >
                CURRENT PRICE
              </span>
              <span
                style={{
                  fontSize: 30,
                  fontFamily: tokens.fontDisplay,
                  color: tokens.textPrimary,
                  lineHeight: 1,
                }}
              >
                {displayData.liveState.currentPrice}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: tokens.textSecondary,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  fontWeight: 600,
                }}
              >
                CURRENT PROFIT
              </span>
              <span
                style={{
                  fontSize: 36,
                  fontFamily: tokens.fontDisplay,
                  color: currentProfitColor,
                  lineHeight: 1,
                }}
              >
                {displayData.currentProfit}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: tokens.textSecondary,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  fontWeight: 600,
                }}
              >
                PRICE CHANGE
              </span>
              <span
                style={{
                  fontSize: 24,
                  fontFamily: tokens.fontDisplay,
                  color: tokens.red,
                  lineHeight: 1,
                }}
              >
                {displayData.currentPriceChange}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: tokens.textSecondary,
                  fontFamily: tokens.fontMono,
                  letterSpacing: '0.15em',
                  fontWeight: 600,
                }}
              >
                BALANCE DISTRIBUTION
              </span>
              <BalancePill
                stableFraction={displayData.liveState.balanceStableFraction}
                width={90}
              />
            </div>
          </div>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }} />

          <div>
            <div
              style={{
                fontSize: 12,
                color: tokens.textPrimary,
                fontFamily: tokens.fontMono,
                letterSpacing: '0.18em',
                marginBottom: 6,
                fontWeight: 700,
              }}
            >
              BALANCES
            </div>
            <DeepRow
              label="Stable balance"
              value={displayData.liveState.stableBalance}
            />
            <DeepRow
              label="Asset balance"
              value={displayData.liveState.assetBalance}
              color={
                displayData.liveState.assetBalance === '0'
                  ? tokens.textMuted
                  : undefined
              }
            />
          </div>

          {hasMrc && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setMrcMode((current) => !current)}
                style={{
                  padding: '6px 12px',
                  background: mrcMode ? 'rgba(240,132,22,0.12)' : 'transparent',
                  color: mrcMode ? tokens.amber : tokens.textMuted,
                  border: mrcMode
                    ? `1px solid ${tokens.amber}66`
                    : '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 3,
                  fontFamily: tokens.fontMono,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: mrcMode ? tokens.amber : tokens.textFaint,
                  }}
                />
                MRC DEEP DIVE · {mrcMode ? 'ON' : 'OFF'}
              </button>
            </div>
          )}

          {mrcMode && mrcEpisode && <MrcPhaseFlow phases={mrcPhases} />}
        </div>

        <div style={rightSide}>
          <div style={{ position: 'relative' }}>
            {mrcMode && mrcEpisode ? (
              <ChartStack
                key="mrc"
                live
                mrcEpisode={mrcEpisode}
                mainHeight={chartHeight}
                rsis={chartRsis}
              />
            ) : (
              <ChartStack
                key="std"
                live
                mainHeight={chartHeight}
                rsis={chartRsis}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveState;
